import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import RefreshIcon from '@mui/icons-material/Refresh';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import config from '../config';
import { useAuth } from '../auth/AuthContext';
import {
  ResizableColGroup,
  ResizableHeaderCell,
  TableEmptyRow,
  TableLoadingRow,
  useResizableColumns,
} from '../components/table/ResizableTable';

const TABLE_COLUMNS = [
  { key: 'name', label: 'Site', minWidth: 180, maxWidth: 420, defaultWidth: 240 },
  { key: 'url', label: 'URL', minWidth: 220, maxWidth: 560, defaultWidth: 340 },
  { key: 'auth', label: 'Auth', minWidth: 150, maxWidth: 280, defaultWidth: 180 },
  { key: 'sync', label: 'Sync Listings', minWidth: 120, maxWidth: 220, defaultWidth: 140 },
  { key: 'last_synced', label: 'Last Synced', minWidth: 170, maxWidth: 320, defaultWidth: 200 },
  { key: 'actions', label: 'Actions', minWidth: 130, maxWidth: 260, defaultWidth: 150 },
];

const AUTH_TYPES = [
  { value: 'wp_password', label: 'WordPress Login' },
  { value: 'application_password', label: 'Application Password' },
  { value: 'consumer_key', label: 'Consumer Key' },
];

const defaultForm = {
  name: '',
  base_url: '',
  auth_type: 'wp_password',
  username: '',
  password: '',
  consumer_key: '',
  consumer_secret: '',
  sync_listings: true,
};

function authTypeLabel(value) {
  return AUTH_TYPES.find((item) => item.value === value)?.label || value || '—';
}

const WooCommerceSitesPage = () => {
  const { fetchWithAuth } = useAuth();
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [form, setForm] = React.useState(defaultForm);
  const [formError, setFormError] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [testingSiteId, setTestingSiteId] = React.useState(null);
  const [testMessage, setTestMessage] = React.useState(null);
  const { getWidth, startColumnResize, tableMinWidth } = useResizableColumns(TABLE_COLUMNS, {
    storageKey: 'table.woocommerce-sites',
  });

  const loadSites = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/woocommerce/sites`);
      if (!resp.ok) {
        const payload = await resp.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to load WooCommerce sites');
      }
      const payload = await resp.json();
      setItems(Array.isArray(payload.items) ? payload.items : []);
    } catch (err) {
      setError(err.message || 'Failed to load WooCommerce sites');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  React.useEffect(() => {
    loadSites();
  }, [loadSites]);

  const updateForm = (field) => (event) => {
    const value = field === 'sync_listings' ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
  };

  const resetDialog = () => {
    setForm(defaultForm);
    setFormError(null);
    setDialogOpen(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/woocommerce/sites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload.error || 'Failed to connect WooCommerce site');
      }
      await loadSites();
      resetDialog();
    } catch (err) {
      setFormError(err.message || 'Failed to connect WooCommerce site');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTest = async (siteId) => {
    setTestingSiteId(siteId);
    setError(null);
    setTestMessage(null);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/woocommerce/sites/${siteId}/test`, {
        method: 'POST',
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload.error || 'Connection test failed');
      }
      setTestMessage('Connection test succeeded.');
    } catch (err) {
      setError(err.message || 'Connection test failed');
    } finally {
      setTestingSiteId(null);
    }
  };

  const usesConsumerKeys = form.auth_type === 'consumer_key';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Typography variant="h5">WooCommerce Sites</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadSites} disabled={loading}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddBusinessIcon />} onClick={() => setDialogOpen(true)}>
            Add Site
          </Button>
        </Stack>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {testMessage ? <Alert severity="success">{testMessage}</Alert> : null}

      <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: tableMinWidth }} stickyHeader>
            <ResizableColGroup columns={TABLE_COLUMNS} getWidth={getWidth} />
            <TableHead>
              <TableRow>
                {TABLE_COLUMNS.map((column) => (
                  <ResizableHeaderCell
                    key={column.key}
                    column={column}
                    getWidth={getWidth}
                    onResizeStart={startColumnResize}
                  />
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableLoadingRow colSpan={TABLE_COLUMNS.length} label="Loading WooCommerce sites..." />
              ) : items.length === 0 ? (
                <TableEmptyRow colSpan={TABLE_COLUMNS.length} label="No WooCommerce sites connected yet." />
              ) : (
                items.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Stack spacing={0}>
                        <Typography variant="subtitle2">{item.name || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.id}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap title={item.url}>
                        {item.url || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0}>
                        <Typography variant="body2">
                          {authTypeLabel(item.account?.auth_type)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {item.account?.username || (item.account?.has_consumer_key ? 'Consumer key saved' : '—')}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{item.sync_listings ? 'Yes' : 'No'}</TableCell>
                    <TableCell>
                      {item.last_synced_at ? new Date(item.last_synced_at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VerifiedUserIcon />}
                        onClick={() => handleTest(item.id)}
                        disabled={testingSiteId === item.id}
                      >
                        {testingSiteId === item.id ? 'Testing…' : 'Test'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={resetDialog} fullWidth maxWidth="sm">
        <DialogTitle>Connect WooCommerce Site</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'grid', gap: 1.5, pt: 0.5 }}>
            <TextField label="Site Name" value={form.name} onChange={updateForm('name')} fullWidth />
            <TextField
              label="Site URL"
              value={form.base_url}
              onChange={updateForm('base_url')}
              placeholder="https://example.com"
              fullWidth
              required
            />
            <TextField
              select
              label="Auth Type"
              value={form.auth_type}
              onChange={updateForm('auth_type')}
              fullWidth
            >
              {AUTH_TYPES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            {usesConsumerKeys ? (
              <>
                <TextField
                  label="Consumer Key"
                  value={form.consumer_key}
                  onChange={updateForm('consumer_key')}
                  fullWidth
                  required
                />
                <TextField
                  label="Consumer Secret"
                  value={form.consumer_secret}
                  onChange={updateForm('consumer_secret')}
                  type="password"
                  fullWidth
                  required
                />
              </>
            ) : (
              <>
                <TextField
                  label="Username"
                  value={form.username}
                  onChange={updateForm('username')}
                  autoComplete="username"
                  fullWidth
                  required
                />
                <TextField
                  label={form.auth_type === 'application_password' ? 'Application Password' : 'Password'}
                  value={form.password}
                  onChange={updateForm('password')}
                  type="password"
                  autoComplete="current-password"
                  fullWidth
                  required
                />
              </>
            )}
            <FormControlLabel
              control={<Checkbox checked={form.sync_listings} onChange={updateForm('sync_listings')} />}
              label="Sync listings for this site"
            />
            {formError ? <Alert severity="error">{formError}</Alert> : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
            {submitting ? 'Connecting…' : 'Connect'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WooCommerceSitesPage;
